import moment, { Duration, Moment } from 'moment'

class Range {
    public static fromParameters = (parameters: { ts?: string; m?: number }): Range =>
        new Range(parameters.ts ? parameters.ts : null, moment.duration('PT' + (parameters.m || 60) + 'M'))

    public static fromStartAndEnd = (start: string, end: string): Range => {
        const startDate = moment(start).utc()
        const endDate = moment(end).utc()

        return new Range(end, moment.duration('PT' + moment.duration(moment(endDate).diff(startDate)).asMinutes() + 'M'))
    }

    private endDate: string | null
    private timeSpan: Duration

    constructor(endDate?: string | null, timeSpan?: Duration) {
        this.endDate = null
        this.timeSpan = moment.duration('PT60M')

        if (endDate) {
            this.setEndDate(endDate)
        }

        if (timeSpan) {
            this.setDuration(timeSpan)
        }
    }

    public setEndDate = (endDate: string | null): void => {
        const end = !endDate || !moment(endDate).isValid() ? moment.utc() : moment.utc(endDate)

        this.endDate = moment
            .utc()
            .subtract(moment.duration(1, 'minutes'))
            .isBefore(end)
            ? null
            : endDate
    }

    public setDuration = (duration: Duration | string): void => {
        this.timeSpan = moment.duration(duration)
    }

    public limitRange = (minimum?: number, maximum?: number): Range => {
        minimum = minimum || 60
        maximum = maximum || 60 * 24

        const minutes = Math.max(minimum, Math.min(maximum, this.timeSpan.asMinutes()))

        return new Range(this.endDate, moment.duration('PT' + minutes + 'M'))
    }

    public asMinutes = (): number => Math.round(this.timeSpan.asMinutes())

    public isCurrent = (): boolean => !this.endDate

    public getStartDate = (): Moment => moment(this.getEndDate()).subtract(this.timeSpan)

    public getEndDate = (): Moment => (this.endDate ? moment.utc(this.endDate) : moment.utc())

    public getDuration = (): Duration => this.timeSpan

    public getPrevious = (): Range => new Range(this.getStartDate().format(), this.timeSpan)

    public getNext = (): Range => {
        let endDate: Moment | null = this.endDate ? moment(this.endDate).add(this.timeSpan) : null

        const differenceToNow = moment.duration(moment().diff(this.getEndDate()))
        if (differenceToNow.asMinutes() < this.timeSpan.asMinutes() * 1.05) {
            endDate = null
        }

        return new Range(endDate?.format(), this.timeSpan)
    }

    public getCacheKey = (): string =>
        (this.endDate
            ? moment(this.endDate)
                  .utc()
                  .format('YYYY-MM-DD-HH-mm')
            : 'now') +
        '-' +
        this.timeSpan.asMinutes()

    public toParameters = (): { ts: string | null; m: number } => ({
        ts:
            this.isCurrent() || !this.endDate
                ? null
                : moment(this.endDate)
                      .utc()
                      .format('YYYY-MM-DD HH:mm'),
        m: Math.ceil(this.timeSpan.asMinutes()),
    })

    public toMinMaxDate = (): { minDate: string; maxDate: string } => ({
        minDate: this.getStartDate().format('YYYY-MM-DD HH:mm:ss'),
        maxDate: moment.utc(this.getEndDate()).format('YYYY-MM-DD HH:mm:ss'),
    })
}

export default Range
